import {
  Box,
  Grid,
  Heading,
  Text,
  HStack,
  Badge,
  Progress,
  VStack,
  Accordion,
} from '@chakra-ui/react';
import type { VoterRecordSummary, CatalistData } from '../../types/contactDetail'

interface VoterRecordSectionProps {
  voterRecord?: VoterRecordSummary
  catalistData?: CatalistData
}

function ScoreBar({ label, score }: { label: string; score?: number }) {
  if (score == null) return null
  return (
    <Box>
      <HStack justify="space-between" mb={1}>
        <Text fontSize="xs" color="gray.600">{label}</Text>
        <Text fontSize="xs" fontWeight="bold">{score}</Text>
      </HStack>
      <Progress.Root value={score} size="sm" colorPalette="brand" borderRadius="full">
        <Progress.Track>
          <Progress.Range />
        </Progress.Track>
      </Progress.Root>
    </Box>
  );
}

function InfoField({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null
  return (
    <Box>
      <Text fontSize="xs" color="gray.500">{label}</Text>
      <Text fontSize="sm">{value}</Text>
    </Box>
  )
}

export function VoterRecordSection({ voterRecord, catalistData }: VoterRecordSectionProps) {
  if (!voterRecord) {
    return (
      <Box bg="surface" p={8} borderRadius="lg" shadow="md" textAlign="center">
        <Text color="gray.500">No voter record linked to this contact.</Text>
      </Box>
    )
  }

  const vr = voterRecord

  return (
    <Box bg="surface" borderRadius="lg" shadow="md" overflow="hidden">
      <Accordion.Root multiple defaultValue={['item-0']}>
        {/* Registration */}
        <Accordion.Item border="none" borderBottom="1px" borderColor="gray.100" value='item-0'>
          <Accordion.ItemTrigger py={3} px={6}>
            <Box flex="1" textAlign="left">
              <Heading size="sm">Registration</Heading>
            </Box>
            <Accordion.ItemIndicator />
          </Accordion.ItemTrigger>
          <Accordion.ItemContent px={6} pb={4}><Accordion.ItemBody>
              <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                <InfoField label="Status" value={vr.registrationStatus} />
                <InfoField label="Party" value={vr.party} />
                <InfoField label="Registration Date"
                  value={vr.registrationDate ? new Date(vr.registrationDate).toLocaleDateString() : undefined}
                />
              </Grid>
            </Accordion.ItemBody></Accordion.ItemContent>
        </Accordion.Item>

        {/* Districts */}
        <Accordion.Item border="none" borderBottom="1px" borderColor="gray.100" value='item-1'>
          <Accordion.ItemTrigger py={3} px={6}>
            <Box flex="1" textAlign="left">
              <Heading size="sm">Districts</Heading>
            </Box>
            <Accordion.ItemIndicator />
          </Accordion.ItemTrigger>
          <Accordion.ItemContent px={6} pb={4}><Accordion.ItemBody>
              <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                <InfoField label="Congressional District" value={vr.congressionalDistrict} />
                <InfoField label="State Senate" value={vr.stateSenateDistrict} />
                <InfoField label="State House" value={vr.stateHouseDistrict} />
                <InfoField label="County District" value={vr.countyDistrict} />
                <InfoField label="Precinct" value={vr.precinctName || vr.precinct} />
              </Grid>
            </Accordion.ItemBody></Accordion.ItemContent>
        </Accordion.Item>

        {/* Scores — sourced from Catalist models when available, falls back to VoterRecord */}
        {(catalistData?.generalActivism != null || catalistData?.ideologyPlus != null || catalistData?.votePropensity != null || vr.partisanScore != null || vr.turnoutScore != null || vr.activismScore != null) && (
          <Accordion.Item border="none" borderBottom="1px" borderColor="gray.100" value='item-2'>
            <Accordion.ItemTrigger py={3} px={6}>
              <Box flex="1" textAlign="left">
                <Heading size="sm">Voter Scores</Heading>
              </Box>
              <Accordion.ItemIndicator />
            </Accordion.ItemTrigger>
            <Accordion.ItemContent px={6} pb={4}><Accordion.ItemBody>
                <VStack gap={3} align="stretch" maxW="400px">
                  <ScoreBar label="Ideology (0=Progressive, 100=Conservative)" score={catalistData?.ideologyPlus ?? vr.partisanScore} />
                  <ScoreBar label="Vote Propensity" score={catalistData?.votePropensity ?? vr.turnoutScore} />
                  <ScoreBar label="General Activism" score={catalistData?.generalActivism ?? vr.activismScore} />
                </VStack>
              </Accordion.ItemBody></Accordion.ItemContent>
          </Accordion.Item>
        )}

        {/* Demographics */}
        <Accordion.Item border="none" borderBottom="1px" borderColor="gray.100" value='item-3'>
          <Accordion.ItemTrigger py={3} px={6}>
            <Box flex="1" textAlign="left">
              <Heading size="sm">Demographics</Heading>
            </Box>
            <Accordion.ItemIndicator />
          </Accordion.ItemTrigger>
          <Accordion.ItemContent px={6} pb={4}><Accordion.ItemBody>
              <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                <InfoField label="Gender" value={vr.gender} />
                <InfoField label="Race/Ethnicity" value={vr.raceEthnicity} />
                <InfoField label="Generation" value={vr.generationCategory} />
                <InfoField label="Occupation" value={vr.occupation} />
                <InfoField label="Employer" value={vr.employer} />
              </Grid>
            </Accordion.ItemBody></Accordion.ItemContent>
        </Accordion.Item>

        {/* Data Source */}
        <Accordion.Item
          border="none"
          borderBottom={catalistData ? "1px" : undefined}
          borderColor="gray.100"
          value='item-4'>
          <Accordion.ItemTrigger py={3} px={6}>
            <Box flex="1" textAlign="left">
              <Heading size="sm">Data Source</Heading>
            </Box>
            <Accordion.ItemIndicator />
          </Accordion.ItemTrigger>
          <Accordion.ItemContent px={6} pb={4}><Accordion.ItemBody>
              <HStack gap={4}>
                <InfoField label="Source" value={vr.dataSource} />
                <InfoField label="Last Updated"
                  value={new Date(vr.updatedAt).toLocaleDateString()}
                />
              </HStack>
            </Accordion.ItemBody></Accordion.ItemContent>
        </Accordion.Item>

        {/* Catalist Demographics */}
        {catalistData && (catalistData.dwid || catalistData.raceModel || catalistData.permanentAbsentee != null) && (
          <Accordion.Item border="none" borderBottom="1px" borderColor="gray.100" value='item-5'>
            <Accordion.ItemTrigger py={3} px={6}>
              <Box flex="1" textAlign="left">
                <Heading size="sm">Catalist Demographics</Heading>
              </Box>
              <Accordion.ItemIndicator />
            </Accordion.ItemTrigger>
            <Accordion.ItemContent px={6} pb={4}><Accordion.ItemBody>
                <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                  <InfoField label="DWID" value={catalistData.dwid} />
                  {catalistData.raceModel && (
                    <Box>
                      <Text fontSize="xs" color="gray.500">Race Model</Text>
                      <Badge colorPalette="purple" variant="subtle" mt={1}>{catalistData.raceModel}</Badge>
                    </Box>
                  )}
                  {catalistData.permanentAbsentee != null && (
                    <InfoField label="Permanent Absentee" value={catalistData.permanentAbsentee ? 'Yes' : 'No'} />
                  )}
                </Grid>
              </Accordion.ItemBody></Accordion.ItemContent>
          </Accordion.Item>
        )}

        {/* Catalist Contact Info */}
        {catalistData && (catalistData.cellPhone || catalistData.landlinePhone || catalistData.registrationStatusReason || catalistData.registrationStatusUpdate) && (
          <Accordion.Item border="none" value='item-6'>
            <Accordion.ItemTrigger py={3} px={6}>
              <Box flex="1" textAlign="left">
                <Heading size="sm">Catalist Contact Info</Heading>
              </Box>
              <Accordion.ItemIndicator />
            </Accordion.ItemTrigger>
            <Accordion.ItemContent px={6} pb={4}><Accordion.ItemBody>
                <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                  <InfoField label="Cell Phone" value={catalistData.cellPhone} />
                  <InfoField label="Landline" value={catalistData.landlinePhone} />
                  <InfoField label="Reg. Status Reason" value={catalistData.registrationStatusReason} />
                  <InfoField label="Reg. Status Update" value={catalistData.registrationStatusUpdate} />
                </Grid>
              </Accordion.ItemBody></Accordion.ItemContent>
          </Accordion.Item>
        )}
      </Accordion.Root>
    </Box>
  );
}
